<template>
    <span
        class="tooltip"
        ref="trigger"
        :aria-describedby="open ? tooltipId : null"
        @mouseenter="show"
        @mouseleave="hide"
        @focusin="show"
        @focusout="onFocusOut"
    >
        <slot></slot>

        <MountingPortal
            v-if="open"
            mountTo=".radiobot-variables"
            append
            :to="portalName"
        >
            <div
                :id="tooltipId"
                ref="bubble"
                class="tooltip__content"
                role="tooltip"
                :data-positioned="positioned"
                :style="contentStyle"
            >
                <slot name="content">{{ text }}</slot>
            </div>
        </MountingPortal>
    </span>
</template>

<script>
import { MountingPortal } from 'portal-vue'

const PLACEMENTS = ['top', 'bottom', 'left', 'right']

export default {
    name: 'tooltipUI',
    components: {
        MountingPortal,
    },
    props: {
        text: {
            type: String,
            default: '',
        },
        placement: {
            type: String,
            default: 'top',
            validator: (value) => PLACEMENTS.includes(value),
        },
        offset: {
            type: Number,
            default: 8,
        },
        disabled: {
            type: Boolean,
            default: false,
        },
    },
    data() {
        return {
            open: false,
            positioned: false,
            listening: false,
            contentStyle: {
                top: '0px',
                left: '0px',
            },
        }
    },
    computed: {
        portalName() {
            return `tooltip-${this._uid}`
        },
        tooltipId() {
            return `tooltip-content-${this._uid}`
        },
    },
    watch: {
        disabled(value) {
            if (value) this.hide()
        },
    },
    beforeDestroy() {
        this.removeGlobalListeners()
    },
    methods: {
        hasContent() {
            const text = (this.text || '').trim()
            if (text.length > 0) return true
            const slot = this.$slots.content
            return !!(slot && slot.length)
        },
        show() {
            if (this.disabled || this.open || !this.hasContent()) return
            this.open = true
            this.positioned = false
            this.$nextTick(() => this.positionWhenReady(0))
        },
        hide() {
            this.open = false
            this.positioned = false
            this.removeGlobalListeners()
        },
        onFocusOut(event) {
            const trigger = this.$refs.trigger
            if (trigger && event.relatedTarget && trigger.contains(event.relatedTarget)) return
            this.hide()
        },
        positionWhenReady(attempt) {
            if (!this.open) return
            if (!this.$refs.bubble) {
                if (attempt > 5) return
                this.$nextTick(() => this.positionWhenReady(attempt + 1))
                return
            }
            this.updatePosition()
            this.addGlobalListeners()
        },
        updatePosition() {
            const trigger = this.$refs.trigger
            const bubble = this.$refs.bubble
            if (!this.open || !trigger || !bubble) return

            const triggerRect = trigger.getBoundingClientRect()
            const bubbleRect = bubble.getBoundingClientRect()
            const offset = this.offset
            let top = 0
            let left = 0

            if (this.placement === 'bottom') {
                top = triggerRect.bottom + offset
                left = triggerRect.left + (triggerRect.width - bubbleRect.width) / 2
            } else if (this.placement === 'left') {
                top = triggerRect.top + (triggerRect.height - bubbleRect.height) / 2
                left = triggerRect.left - bubbleRect.width - offset
            } else if (this.placement === 'right') {
                top = triggerRect.top + (triggerRect.height - bubbleRect.height) / 2
                left = triggerRect.right + offset
            } else {
                top = triggerRect.top - bubbleRect.height - offset
                left = triggerRect.left + (triggerRect.width - bubbleRect.width) / 2
            }

            const margin = 8
            const maxLeft = Math.max(margin, window.innerWidth - bubbleRect.width - margin)
            const maxTop = Math.max(margin, window.innerHeight - bubbleRect.height - margin)
            left = Math.min(Math.max(left, margin), maxLeft)
            top = Math.min(Math.max(top, margin), maxTop)

            this.contentStyle = {
                top: `${top}px`,
                left: `${left}px`,
            }
            this.positioned = true
        },
        addGlobalListeners() {
            if (this.listening) return
            this.listening = true
            window.addEventListener('resize', this.updatePosition)
            window.addEventListener('scroll', this.updatePosition, true)
        },
        removeGlobalListeners() {
            if (!this.listening) return
            this.listening = false
            window.removeEventListener('resize', this.updatePosition)
            window.removeEventListener('scroll', this.updatePosition, true)
        },
    },
}
</script>

<style scoped>
.tooltip {
    display: inline-flex;
}

.tooltip__content {
    position: fixed;
    z-index: 3000;
    visibility: hidden;
    width: max-content;
    max-width: 240px;
    box-sizing: border-box;
    padding: var(--spacing-sm);
    background: var(--background-white);
    color: var(--color-primary);
    border-radius: var(--radius-md);
    box-shadow: 0 2px 4px #00000066;
    font-family: var(--font-family-primary);
    font-size: var(--text-xs);
    text-align: center;
    line-height: var(--leading-normal);
    pointer-events: none;
}

.tooltip__content[data-positioned="true"] {
    visibility: visible;
    animation: fadeIn 0.2s ease-in;
}

@keyframes fadeIn {
    from { opacity: 0; }
    to { opacity: 1; }
}
</style>

<style>
.radiobot-variables > .vue-portal-target {
    position: absolute;
    width: 0;
    height: 0;
    overflow: visible;
    pointer-events: none;
}
</style>
